/**
 * useAuthErrorHandler - Handles auth error events at the app level
 *
 * GW-2099: Listens for 'session-expired' events emitted by the API client
 * or getAuthToken() when a stale Cognito token is detected. Clears auth state,
 * shows a toast, and redirects to the login page.
 *
 * Mount this hook once in a top-level component (e.g., App.tsx).
 */

import { useCallback, useEffect, useRef } from 'react';

import { toast } from 'sonner';

import { authEvents } from '@/lib/authEvents';
import { useAuthStore } from '@/stores/store';

export function useAuthErrorHandler(): void {
  const { signOut, isAuthenticated } = useAuthStore();
  const handlingRef = useRef(false);

  const handleSessionExpired = useCallback(async () => {
    // Guard against multiple simultaneous triggers
    if (handlingRef.current || !isAuthenticated) return;
    handlingRef.current = true;

    try {
      await signOut();
      toast.error('Your session has expired. Please sign in again.');

      // GW-4768: Preserve current URL as redirect param unless already on auth pages
      const currentPath = window.location.pathname + window.location.search + window.location.hash;
      const isOnAuthPage = window.location.pathname.startsWith('/auth/');
      const shouldIncludeRedirect = !isOnAuthPage && currentPath !== '/';

      const loginUrl = shouldIncludeRedirect
        ? `/auth/login?redirect=${encodeURIComponent(currentPath)}`
        : '/auth/login';

      window.location.href = loginUrl;
    } finally {
      handlingRef.current = false;
    }
  }, [signOut, isAuthenticated]);

  useEffect(() => {
    authEvents.on('session-expired', handleSessionExpired);
    return () => {
      authEvents.off('session-expired', handleSessionExpired);
    };
  }, [handleSessionExpired]);
}
